/* ===== External libraries ===== */
import { AxiosResponse, AxiosError } from 'axios';

/* ===== Shared utilities ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, UUID } from '@umanforge/common';

/* ===== Internal modules ===== */
import { Dispatch } from '@reduxjs/toolkit';
import { GlobalService } from './global.service';
import { ToastService } from 'services/toast.service';
import { API } from 'constants/api.contants';
import { ChartData, ChartFilter, ChartOption } from 'interfaces/chart.interface';

export class ChartService {

  static getChartsByDatesAndPlanningId(
    planningId: UUID,
    body: {
      dateStart: string,
      dateEnd: string,
      timezone?: string,
      filters?: ChartFilter
    },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ChartData[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.CHART_BY_DATES_AND_PLANNING_ID}/${planningId}`, { ...await CommonGlobalService.getHeaderRequest( token ), params: body } )
        .then( ( response: AxiosResponse<ChartData[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getExtractionByDatesAndPlanningId(
    planningId: UUID,
    body: {
      dateStart: string,
      dateEnd: string,
      timezone?: string,
      filters?: ChartFilter
    },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Blob | null> {
    return new Promise( async resolve => {
      const toastService = new ToastService( 'ChartService' );

      AXIOS.get( `${API.EXTRACTION_BY_DATES_AND_PLANNING_ID}/${planningId}`, {
        ...await CommonGlobalService.getHeaderRequest( token ),
        params: body,
        responseType: 'blob'
      } )
        .then( async ( response: AxiosResponse<Blob> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status == 202 ) {
            if ( onWarning ) {
              onWarning( response );
            }
            else if ( response.data instanceof Blob ) {
              try {
                const rawText = await response.data.text();
                const parsed = JSON.parse( rawText );
                const message = parsed?.msg ?? parsed?.message ?? 'No content in file';
                toastService.showWarning( message );
              }
              catch {
                toastService.showWarning( 'No content in file' );
              }
            }
            else {
              GlobalService.onAxiosWarning( response, onWarning );
            }
            return resolve( null );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status != 204 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = 'PL_STATISTIQUES_DATA.xlsx';
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          } else {
            resolve( null );
            return;
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getBarOption( title: string ): ChartOption {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      yAxis: {
        type: 'value'
      },
      title: {
        text: title,
        left: 'center',
      },
      legend: {
        bottom: 0,
      },
      grid: {
        left: 50,
        right: 50,
        top: 30,
        bottom: 55
      },
      series: []
    };
  }

  static getPieOption( title: string ): ChartOption {
    return {
      tooltip: {
        trigger: 'item'
      },
      legend: {
        bottom: 0,
      },
      title: {
        text: title,
        left: 'center',
        textStyle: { fontSize: 18, fontWeight: 'bold' }
      },
      series: []
    };
  }

}
